
export interface StoryConfig {
  prompt: string;
  genre: string;
  tone: string;
  illustrationStyle: string;
  length: 'Short' | 'Medium' | 'Long';
  targetAudience: string;
}

export interface StoryDNA {
  childName: string;
  childAge: number;
  childGender?: string;
  audienceAge: number;
  tone: 'calmingBedtime' | 'cozy' | 'funny' | 'adventure' | 'mystery' | 'gentleFantasy';
  genre: 'fantasy' | 'sciFi' | 'animals' | 'everydayLife' | 'folkloreInspired';
  emotionalGoal: 'confidence' | 'kindness' | 'courage' | 'patience' | 'gratitude' | 'curiosity';
  values: string[];
  setting: string;
  motifs: string[];
  complexityProfile: {
    ageGroup: string;
    pageCount: number;
    styleRules: string;
  };
  constraints: {
    maxPages: number;
    readingLevelNotes: string;
    safetyNotes: string;
  };
  visualProfile?: {
    photoProvided: boolean;
    visualNotes: string;
  };
}

export interface CriticScorecard {
  scores: {
    emotionalAuthenticity: number;
    ageAppropriate: number;
    originality: number;
    narrativeCoherence: number;
    parentTrust: number;
    visualConsistency: number;
  };
  needsRevision: boolean;
  revisionInstructions: string;
  overallNotes: string;
}

/** Character appearance details for consistent visual representation */
export interface CharacterAppearance {
  coat_or_skin: string;
  color_palette: string;
  distinctive_marks: string[];
  clothing_or_accessories: string[];
}

/** Single character definition with locked traits */
export interface CharacterDefinition {
  name: string;
  type: 'animal' | 'human' | 'fantasy';
  species: string | null;
  breed: string | null;
  age: string | null;
  appearance: CharacterAppearance;
  personality: string[];
  do_not_change: string[];
}

/** Complete Character Bible for stable character representation across story pages */
export interface CharacterBible {
  main: CharacterDefinition;
  supporting: CharacterDefinition[];
  style_lock: {
    art_style: string;
    camera_rules: string[];
    consistency_rules: string[];
  };
}

export interface BookPage {
  pageNumber: number;
  text: string;
  imagePrompt: string;
  layout: 'text-left' | 'text-right';
  imageUrl?: string;
  audioBase64?: string;
}

export interface GeneratedBook {
  id?: string;
  userId?: string;
  createdAt?: string;
  title: string;
  synopsis: string;
  characterBible: CharacterBible;
  dna?: StoryDNA;
  coverImagePrompt: string;
  coverImageUrl?: string;
  pages: BookPage[];
  estimatedReadingTime: string;
  tags: string[];
  illustrationStyle?: string;
  criticScorecard?: CriticScorecard;
  authorName?: string;
  voiceName?: string;
}

export type GeneratedStory = GeneratedBook;

export interface User {
  id: string;
  name: string;
  email: string;
  isPro: boolean;
  isVerified: boolean;
  dailyStoryCount: number;
  lastStoryDate: string;
}

export interface Subscription {
  user_id: string;
  provider: 'polar';
  status: 'active' | 'trialing' | 'canceled' | 'past_due' | 'incomplete';
  plan: 'monthly' | 'yearly';
  current_period_end: string;
  cancel_at_period_end: boolean;
}

export interface ChildProfile {
  userId: string;
  name: string;
  authorName: string;
  age: number;
  sex: string;
  appearance: string;
  photoBase64: string;
}

export interface VoiceOption {
  id: string;
  displayName: string;
  description: string;
  provider: 'system';
  isCloned: boolean;
}

export const SYSTEM_VOICES: VoiceOption[] = [
  { id: 'Kore', displayName: 'Kore', description: 'Warm & expressive', provider: 'system', isCloned: false },
  { id: 'Puck', displayName: 'Puck', description: 'Playful & bright', provider: 'system', isCloned: false },
  { id: 'Charon', displayName: 'Charon', description: 'Deep & soothing', provider: 'system', isCloned: false },
  { id: 'Fenrir', displayName: 'Fenrir', description: 'Strong & clear', provider: 'system', isCloned: false },
  { id: 'Zephyr', displayName: 'Zephyr', description: 'Gentle & calm', provider: 'system', isCloned: false },
];

export interface Child {
  id: string;
  userId: string;
  name: string;
  authorName: string;
  age: number;
  sex: string;
  appearance: string;
  photoBase64: string;
}

export interface ParentControls {
  userId: string;
  maxStoryLength: 'Short' | 'Medium' | 'Long';
  excludedThemes: string[];
  monthlyStoryLimit: number;
  dailySoftLimit: number;
}

export type ViewState = 'landing' | 'create' | 'reading' | 'login' | 'signup' | 'library' | 'profile-setup' | 'verify-email' | 'privacy' | 'coppa' | 'support' | 'subscription-success' | 'subscription-manage' | 'parent-dashboard' | 'blog-how-it-works' | 'blog-best-apps' | 'blog-screen-free' | 'blog-safety-guide';

export const GENRES = [
  'Fairy Tale', 'Space Adventure', 'Magical Forest', 'Animal Friends', 'Underwater', 'Superhero', 'Bedtime Story', 'Dinosaur'
];

export const TONES = [
  'Whimsical', 'Calm', 'Funny', 'Adventurous', 'Sweet', 'Mysterious', 'Educational'
];

export const AUDIENCES = [
  'Toddler (2-4)', 'Little Kid (5-7)', 'Big Kid (8-10)'
];

export const ILLUSTRATION_STYLES = [
  'Watercolor & Ink',
  '3D Pixar Style',
  'Storybook Flat',
  'Vintage Pencil',
  'Studio Ghibli Anime',
  'Oil Painting',
  'Paper Cutout'
];
